import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Edit2, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import MainLayout from "@/components/layout/MainLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateString } from "@/lib/utils";

const Dashboard = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Form state
  const [name, setName] = useState("");
  const [constitutionDate, setConstitutionDate] = useState("");
  const [status, setStatus] = useState("Pre-Setup");
  const [missionStatement, setMissionStatement] = useState("");

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['dashboard', user?.id],
    queryFn: fetchDashboardData,
    enabled: !!user,
  });

  useEffect(() => {
    if (!user) return;
    
    // Set up real-time subscriptions
    const channel = supabase
      .channel('dashboard-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'unas' }, () => {
        queryClient.invalidateQueries({ queryKey: ['dashboard', user.id] });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  async function fetchDashboardData() {
    // Fetch UNA data
    const { data: una, error: unaError } = await supabase
      .from('unas')
      .select('*')
      .eq('treasury_secretary_id', user?.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (unaError && unaError.code !== 'PGRST116') throw unaError;

    // Fetch fulfillment progress with onboarding data
    const { data: progress, error: progressError } = await supabase
      .from('user_fulfillment_progress')
      .select('*')
      .eq('user_id', user?.id)
      .maybeSingle();

    if (progressError && progressError.code !== 'PGRST116') throw progressError;

    return {
      unaData: una,
      progressData: progress,
    };
  }

  const { unaData, progressData } = dashboardData || {};

  // Populate form when UNA data loads
  useEffect(() => {
    if (unaData) {
      setName(unaData.name || "");
      setConstitutionDate(unaData.constitution_date || "");
      setStatus(unaData.status || "Pre-Setup");
      setMissionStatement(unaData.mission_statement || "");
    }
  }, [unaData]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("UNA name is required");
      return;
    }

    setIsSaving(true);
    try {
      if (unaData?.id) {
        // Update existing UNA
        const { error } = await supabase
          .from('unas')
          .update({
            name: name.trim(),
            constitution_date: constitutionDate || null,
            status,
            mission_statement: missionStatement.trim() || null,
          })
          .eq('id', unaData.id);

        if (error) throw error;
        toast.success("UNA updated successfully");
      } else {
        // Create new UNA
        const { error } = await supabase
          .from('unas')
          .insert({
            name: name.trim(),
            constitution_date: constitutionDate || null,
            status,
            mission_statement: missionStatement.trim() || null,
            treasury_secretary_id: user?.id,
          });

        if (error) throw error;
        toast.success("UNA created successfully");
      }
      
      queryClient.invalidateQueries({ queryKey: ['dashboard', user?.id] });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving UNA:', error);
      toast.error("Failed to save UNA");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-ontraport', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast.success("Your account is up to date.");
        queryClient.invalidateQueries({ queryKey: ['dashboard', user?.id] });
      } else if (data?.not_found) {
        toast.info(data?.message || "Your account is not connected yet. Some parts may be limited.");
      } else {
        throw new Error(data?.error || data?.message || 'Update failed');
      }
    } catch (error: any) {
      console.error('Sync error:', error);
      toast.error(error.message || "Could not update your account. Please try again.");
    } finally {
      setIsSyncing(false);
    }
  };

  const onboardingData = progressData?.onboarding_data as Record<string, any> || {};

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-8">
        {/* Welcome Header */}
        <div className="space-y-2">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">
            Welcome back, {user?.user_metadata?.full_name || user?.email?.split('@')[0]}
          </h1>
          {unaData && (
            <p className="text-muted-foreground">
              {unaData.name} · {unaData.primary_officer_role || 'Secretary Treasurer'}
            </p>
          )}
        </div>

        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">
                  {unaData ? 'Your UNA' : 'Create Your UNA'}
                </CardTitle>
                <CardDescription>
                  {unaData ? 'Manage your UNA information' : 'Set up your unincorporated association'}
                </CardDescription>
              </div>
              {unaData && !isEditing && (
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!unaData || isEditing ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="una-name">UNA Name *</Label>
                  <Input
                    id="una-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter UNA name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="constitution-date">Constitution Date</Label>
                  <Input
                    id="constitution-date"
                    type="date"
                    value={constitutionDate}
                    onChange={(e) => setConstitutionDate(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pre-Setup">Pre-Setup</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mission">Mission Statement</Label>
                  <Textarea
                    id="mission"
                    value={missionStatement}
                    onChange={(e) => setMissionStatement(e.target.value)}
                    placeholder="Describe your UNA's mission and purpose"
                    rows={4}
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSave} disabled={isSaving} className="flex-1">
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? "Saving..." : "Save"}
                  </Button>
                  {isEditing && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEditing(false);
                        // Reset form
                        if (unaData) {
                          setName(unaData.name || "");
                          setConstitutionDate(unaData.constitution_date || "");
                          setStatus(unaData.status || "Pre-Setup");
                          setMissionStatement(unaData.mission_statement || "");
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="py-3 border-b">
                  <p className="text-sm text-muted-foreground mb-1">Name</p>
                  <p className="text-lg font-medium">{unaData.name}</p>
                </div>

                {unaData.constitution_date && (
                  <div className="py-3 border-b">
                    <p className="text-sm text-muted-foreground mb-1">Constitution Date</p>
                    <p className="text-base">
                      {formatDateString(unaData.constitution_date)}
                    </p>
                  </div>
                )}

                <div className="py-3 border-b">
                  <p className="text-sm text-muted-foreground mb-1">Status</p>
                  <p className="text-base font-medium text-accent">{unaData.status}</p>
                </div>

                {unaData.mission_statement && (
                  <div className="py-3">
                    <p className="text-sm text-muted-foreground mb-1">Mission Statement</p>
                    <p className="text-base leading-relaxed">{unaData.mission_statement}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* UNA Administration Details - Strategy Intake Data */}
        {progressData && Object.keys(onboardingData).length > 0 && (
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl">UNA Administration Details</CardTitle>
                  <CardDescription>
                    Information from your strategy intake form
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleSync}
                  disabled={isSyncing}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                  {isSyncing ? 'Updating...' : 'Update'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Entity Management Section */}
              {(onboardingData.una_administration || onboardingData.entities_to_transfer || onboardingData.new_entities) && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground border-b pb-2">Entity Management</h3>
                  
                  {onboardingData.una_administration && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">UNA Administration</p>
                      <p className="text-base whitespace-pre-line">{onboardingData.una_administration}</p>
                    </div>
                  )}
                  
                  {onboardingData.entities_to_transfer && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Entities to Transfer Ownership</p>
                      <p className="text-base whitespace-pre-line">{onboardingData.entities_to_transfer}</p>
                    </div>
                  )}
                  
                  {onboardingData.new_entities && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">New Entities to Form</p>
                      <p className="text-base whitespace-pre-line">{onboardingData.new_entities}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Asset Details Section */}
              {(onboardingData.other_assets_1 || onboardingData.other_assets_2 || onboardingData.other_assets_3) && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground border-b pb-2">Asset Details</h3>
                  
                  {onboardingData.other_assets_1 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Other Assets 1</p>
                      <p className="text-base whitespace-pre-line">{onboardingData.other_assets_1}</p>
                    </div>
                  )}
                  
                  {onboardingData.other_assets_2 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Other Assets 2</p>
                      <p className="text-base whitespace-pre-line">{onboardingData.other_assets_2}</p>
                    </div>
                  )}
                  
                  {onboardingData.other_assets_3 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Other Assets 3</p>
                      <p className="text-base whitespace-pre-line">{onboardingData.other_assets_3}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Planning Considerations Section */}
              {(onboardingData.primary_goals || onboardingData.estate_planning_entity) && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground border-b pb-2">Planning Considerations</h3>
                  
                  {onboardingData.primary_goals && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Primary Goals</p>
                      <p className="text-base whitespace-pre-line">{onboardingData.primary_goals}</p>
                    </div>
                  )}
                  
                  {onboardingData.estate_planning_entity && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Estate Planning Entity</p>
                      <p className="text-base whitespace-pre-line">{onboardingData.estate_planning_entity}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Additional Information Section */}
              {(onboardingData.other_considerations || onboardingData.special_circumstances || onboardingData.additional_info) && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground border-b pb-2">Additional Information</h3>
                  
                  {onboardingData.other_considerations && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Other Considerations</p>
                      <p className="text-base whitespace-pre-line">{onboardingData.other_considerations}</p>
                    </div>
                  )}
                  
                  {onboardingData.special_circumstances && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Special Circumstances</p>
                      <p className="text-base whitespace-pre-line">{onboardingData.special_circumstances}</p>
                    </div>
                  )}
                  
                  {onboardingData.additional_info && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Additional Info</p>
                      <p className="text-base whitespace-pre-line">{onboardingData.additional_info}</p>
                    </div>
                  )}
                </div>
              )}

              {progressData.last_ontraport_sync && (
                <div className="pt-4 border-t">
                  <p className="text-xs text-muted-foreground">
                    Last updated: {new Date(progressData.last_ontraport_sync).toLocaleString()}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default Dashboard;